import { matchOrgDetailPath, ROUTES } from '@futrli/shared-services';

import {
  SyncControlDatasourceState,
  SyncControlState
} from '../../forecast/detail/reports/datasource-sync/sync-control/types';
import { SyncContextType } from './types';

/**
 * Extracts workspaceId and organisationId from the current URL using the shared organisation detail route matcher.
 * Also detects consolidation routes — sync is not applicable to consolidations.
 * @param pathname The current URL pathname
 * @returns An object containing workspaceId and organisationId (or empty strings), plus isConsolidation flag
 */
export const extractIdsFromPathname = (
  pathname: string
): { workspaceId: string; organisationId: string; isConsolidation: boolean } => {
  if (pathname.includes(ROUTES.CONSOLIDATIONS_ROUTE)) {
    return { workspaceId: '', organisationId: '', isConsolidation: true };
  }

  const orgMatch = matchOrgDetailPath(pathname, false);
  if (orgMatch?.params.workspaceId && orgMatch?.params.organisationId) {
    return {
      workspaceId: orgMatch.params.workspaceId,
      organisationId: orgMatch.params.organisationId,
      isConsolidation: false
    };
  }

  return { workspaceId: '', organisationId: '', isConsolidation: false };
};

// Stable no-op references used by DEFAULT_SYNC_CONTEXT to avoid allocating new functions on every render.
const noop = () => {
  return;
};
const asyncNoop = async () => {
  return;
};

// Static "inactive" context value provided on routes where sync is not applicable
// (e.g. consolidations, missing IDs). This allows descendants to call useSyncContext()
// without crashing, while SyncContextProviderInner (and its data-fetching hooks) stays unmounted.
export const DEFAULT_SYNC_CONTEXT: SyncContextType = {
  datasource: undefined,
  excelFiles: undefined,
  isLoadingDatasource: false,
  error: false,
  syncControlState: SyncControlState.READY,
  datasourceState: SyncControlDatasourceState.NOT_CONNECTED,
  isSyncing: false,
  hasOauth2Error: false,
  needsReconnect: false,
  syncTimestamp: '',
  handleOauth2SyncNow: asyncNoop,
  loadData: asyncNoop,
  setPolling: noop,
  isReconnecting: false,
  reconnect: asyncNoop,
  workspaceId: '',
  organisationId: '',
  organisation: null,
  integration: undefined,
  financialConfig: undefined,
  isDrawerOpen: false,
  openDrawer: noop,
  closeDrawer: noop,
  syncResultType: null,
  setSyncResultType: noop
};
